/**
 * The way the model manager now works is by rendering 
 * the current state of the bike from the garage 
 * instead of trying to track its own internal state.
 * 
 * this greatly simplifies the code for showing the bike
 * and for the data structures required to manage the visibility of nodes.
 */
export class ModelManager {
  constructor(assetManager) {
    this._assetManager = assetManager;
    this._sketchfabAPIUtility = null;
    // add the model manager to the window so we can access it in console debugging
    window._modelManager = this;
  }

  initialize(sketchfabAPIUtility) {
    this._sketchfabAPIUtility = sketchfabAPIUtility;
  }

  reinitialize() {
    let sketchfabId = this._assetManager.getSketchfabId();
    this._sketchfabAPIUtility = new window.SketchfabAPIUtility(
      sketchfabId,
      this._sketchfabAPIUtility.iframe,
      {
        autostart: 1,
        autospin: 0,
        ui_infos: 0,
        ui_general_controls: 1,
        ui_settings: 0,
        ui_help: 0,
        ui_hint: 0,
        ui_stop: 0,
        ui_inspector: 0,
        ui_vr: 0,
        ui_fullscreen: 0,
        ui_watermark_link: 0,
        preload: 1,
      }
    );
    this._sketchfabAPIUtility.addEventListener(
      this._sketchfabAPIUtility.EVENT_INITIALIZED,
      this._onSketchfabUtilityReady
    );
    this._sketchfabAPIUtility.create();
  };

  _onSketchfabUtilityReady = () => {
    this._sketchfabAPIUtility.removeEventListener(
      this._sketchfabAPIUtility.EVENT_INITIALIZED,
      this._onSketchfabUtilityReady
    );
    this.hideAllParts();
  };

  showBike(bike) {
    this.showBikeRecursive(bike)
  }

  showBikeFlat(bike) {
    this.hideAllParts();

    // turn the bike parts into a hash so that its easier to look up parts for conflicts
    /** A map containing all the bike parts @type {Map<string, part>} */
    const partMap = bike.parts.reduce(
      /** @param {Map} acc - The accumulator map to add parts to */
      (acc, part) => {
        acc.set(part.sku, part);
        return acc;
    }, new Map());
    
    for (const part of bike.parts) {
      const geometry = part.geometry;
      if (geometry === undefined) {
        console.log("missing geometry for:", part.sku);
        continue;
      }
      console.log("[modelmanager] part: ", part.sku, part.name)
      
      const variantsToApply = Object.keys(geometry.variants || {}).filter((k) => partMap.has(k));
      if (variantsToApply.length > 0) {
        for (const variantSku of variantsToApply) {
          const variant = geometry.variants[variantSku];
          for (const geometryName of variant.add) {
            console.log("[modelmanager] add: ", geometryName)
            this._sketchfabAPIUtility.setNodeVisibility(geometryName, 1);
          }
          for (const geometryName of variant.remove) {
            console.log("[modelmanager] remove: ", geometryName)
            this._sketchfabAPIUtility.setNodeVisibility(geometryName, 0);
          }
        }
      } else {
        for (const geometryName of (geometry.add || [])) {
          console.log("[modelmanager] add: ", geometryName)
          this._sketchfabAPIUtility.setNodeVisibility(geometryName, 1);
        }
        for (const geometryName of (geometry.remove || [])) {
          console.log("[modelmanager] remove: ", geometryName)
          this._sketchfabAPIUtility.setNodeVisibility(geometryName, 0);
        }
      }
    }
    // do it.
  }

  showBikeRecursive(bike) {
    this.hideAllParts();

    // turn the bike parts into a hash so that its easier to look up parts for conflicts
    /** A map containing all the bike parts @type {Map<string, part>} */
    const partMap = bike.parts.reduce(
      /** @param {Map} acc - The accumulator map to add parts to */
      (acc, part) => {
        acc.set(part.sku, part);
        return acc;
    }, new Map());
    
    const applyVariants = (geometry, depth) => {
      // depth first recursive search for valid variants
      if (geometry.variants !== undefined && geometry.variants.variants !== undefined) {
        if (applyVariants(geometry.variants, depth + 1)) {
          return true;
        }
      }
      const variantsToApply = Object.keys(geometry.variants || {}).filter((k) => partMap.has(k));
      if (variantsToApply.length > 0) {
        for (const variantSku of variantsToApply) {
          const variant = geometry.variants[variantSku];
          for (const geometryName of variant.add || []) {
            console.log(`[modelmanager][${depth}] add: `, geometryName)
            this._sketchfabAPIUtility.setNodeVisibility(geometryName, 1);
          }
          for (const geometryName of variant.remove  || []) {
            console.log(`[modelmanager][${depth}] remove: `, geometryName)
            this._sketchfabAPIUtility.setNodeVisibility(geometryName, 0);
          }
        }
        return true
      } else if (geometry.add != undefined) {
        for (const geometryName of (geometry.add || [])) {
          console.log(`[modelmanager][${depth}] add: `, geometryName)
          this._sketchfabAPIUtility.setNodeVisibility(geometryName, 1);
        }
        for (const geometryName of (geometry.remove || [])) {
          console.log(`[modelmanager][${depth}] remove: `, geometryName)
          this._sketchfabAPIUtility.setNodeVisibility(geometryName, 0);
        }
        return true
      }
      return false
    }

    for (const part of bike.parts) {
      const geometry = part.geometry;
      if (geometry === undefined) {
        console.log("missing geometry for:", part.sku);
        continue;
      }
      console.log("[modelmanager] part: ", part.sku, part.name)
      // do it.
      applyVariants(part.geometry, 0);
    }
  }

  hideNode(nodeName) {
    this._sketchfabAPIUtility
  }

  showModel(model) {
  }

  hideAllParts() {
    const nodeHash = this._sketchfabAPIUtility.nodeHash;
    console.dir(nodeHash);
    console.log("======");
    const nodeNames = Object.keys(nodeHash.Group);
    for (const nodeName of nodeNames) {
      this._sketchfabAPIUtility.setNodeVisibility(nodeName, 0);
    }
  }

  setCameraPosition(pos) {
    // Change Camera
    const coords = pos.split(",");
    const position = {
      x: coords[0],
      y: coords[1],
      z: coords[2],
    };
    const target = {
      x: coords[3],
      y: coords[4],
      z: coords[5],
    };

    this._sketchfabAPIUtility.setCamera(position, target, 1.5);
  }

  resetCamera() {
    if (this._sketchfabAPIUtility) {
      this.setCameraPosition("-238.32, 274.36, -4936.1,-11.4, 0, -4998.9");
    }
  }

  printModel() {
    const nodeHash = this._sketchfabAPIUtility.nodeHash
    console.dir(nodeHash);
    const nodeNames = Object.keys(nodeHash.Group);
    console.log("== Group Nodes ==")
    for (const nodeName of nodeNames) {
      console.log(nodeName)
    }
  }
}


// WEBPACK FOOTER //
// ./src/managers/Model/ModelManager.js